import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const AttendanceCard = ({ userAttendance, onAttendanceChange, matchData }) => {
  const [isUpdating, setIsUpdating] = useState(false);

  const handleAttendanceUpdate = async (status) => {
    setIsUpdating(true);
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 800));
    
    onAttendanceChange?.(status);
    setIsUpdating(false);
  };

  const getAttendanceStatus = () => {
    switch (userAttendance) {
      case 'confirmed':
        return {
          icon: 'CheckCircle',
          text: 'Presença confirmada',
          color: 'text-success',
          bgColor: 'bg-success/10',
          description: 'Você confirmou presença nesta pelada'
        };
      case 'declined':
        return {
          icon: 'XCircle',
          text: 'Não vou participar',
          color: 'text-error',
          bgColor: 'bg-error/10',
          description: 'Você declinou o convite para esta pelada'
        };
      default:
        return {
          icon: 'Clock',
          text: 'Aguardando confirmação',
          color: 'text-warning',
          bgColor: 'bg-warning/10',
          description: 'Confirme sua presença o quanto antes'
        };
    }
  };

  const status = getAttendanceStatus();

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-2 p-4 md:p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-heading font-semibold text-card-foreground mb-2">
            Sua Participação
          </h3>
          <div className="flex items-center space-x-2">
            <div className={`flex items-center justify-center w-6 h-6 rounded-full ${status?.bgColor}`}>
              <Icon name={status?.icon} size={14} className={status?.color} />
            </div>
            <div>
              <p className={`text-sm font-medium ${status?.color}`}>
                {status?.text}
              </p>
              <p className="text-xs text-muted-foreground">
                {status?.description}
              </p>
            </div>
          </div>
        </div>

        {/* Quick Status Toggle - Mobile */}
        <div className="md:hidden">
          {userAttendance === 'confirmed' ? (
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={() => handleAttendanceUpdate('declined')}
              loading={isUpdating}
              className="text-error hover:bg-error/10"
            />
          ) : (
            <Button
              variant="ghost"
              size="sm"
              iconName="Check"
              onClick={() => handleAttendanceUpdate('confirmed')}
              loading={isUpdating}
              className="text-success hover:bg-success/10"
            />
          )}
        </div>
      </div>
      {/* Attendance Actions */}
      <div className="space-y-3">
        {/* Primary Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {userAttendance !== 'confirmed' && (
            <Button
              variant="default"
              fullWidth
              iconName="Check"
              iconPosition="left"
              onClick={() => handleAttendanceUpdate('confirmed')}
              loading={isUpdating}
              className="bg-success hover:bg-success/90 text-success-foreground"
            >
              {isUpdating ? 'Confirmando...' : 'Confirmar Presença'}
            </Button>
          )}
          
          {userAttendance !== 'declined' && (
            <Button
              variant={userAttendance === 'confirmed' ? 'default' : 'outline'}
              fullWidth
              iconName="X"
              iconPosition="left"
              onClick={() => handleAttendanceUpdate('declined')}
              loading={isUpdating}
              className={userAttendance === 'confirmed' ? 'bg-error hover:bg-error/90 text-error-foreground' : 'text-error hover:bg-error/10 border-error/20'}
            >
              {isUpdating ? 'Atualizando...' : userAttendance === 'confirmed' ? 'Cancelar Presença' : 'Não Posso Ir'}
            </Button>
          )}
        </div>

        {/* Maybe/Tentative Option */}
        {userAttendance !== 'pending' && (
          <Button
            variant="ghost"
            fullWidth
            size="sm"
            iconName="Clock"
            iconPosition="left"
            onClick={() => handleAttendanceUpdate('pending')}
            loading={isUpdating}
            className="text-warning hover:bg-warning/10"
          >
            Talvez / Decidir depois
          </Button>
        )}
      </div>
      {/* Additional Information */}
      {userAttendance === 'confirmed' && matchData && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-lg font-bold text-card-foreground">
                {matchData?.confirmedPlayers?.findIndex(p => p?.id === 'currentUser') + 1 || matchData?.confirmedPlayers?.length + 1}
              </div>
              <div className="text-xs text-muted-foreground">
                Posição na lista
              </div>
            </div>
            
            <div>
              <div className="text-lg font-bold text-card-foreground">
                R$ {matchData?.pricePerPlayer?.toFixed(2)}
              </div>
              <div className="text-xs text-muted-foreground">
                Valor a pagar
              </div>
            </div>
            
            <div>
              <div className="text-lg font-bold text-card-foreground">
                {matchData?.maxPlayers - (matchData?.confirmedPlayers?.length || 0)}
              </div>
              <div className="text-xs text-muted-foreground">
                Vagas restantes
              </div>
            </div>
          </div>

          {/* Payment Status */}
          <div className="mt-4 p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Icon name="CreditCard" size={16} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Status do pagamento</span>
              </div>
              <span className="text-sm font-medium text-warning">
                Pendente
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Você pode pagar via PIX ou cartão na seção de pagamento
            </p>
          </div>
        </div>
      )}
      {/* Declined Reason (if user declined) */}
      {userAttendance === 'declined' && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="bg-error/5 border border-error/20 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <Icon name="Info" size={16} className="text-error mt-0.5" />
              <div>
                <p className="text-sm font-medium text-error mb-1">
                  Você não vai participar desta pelada
                </p>
                <p className="text-xs text-muted-foreground">
                  Ainda pode mudar de ideia e confirmar sua presença até o início do jogo
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Pending Reminder */}
      {userAttendance === 'pending' && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="bg-warning/5 border border-warning/20 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
              <div>
                <p className="text-sm font-medium text-warning mb-1">
                  Confirme sua presença o quanto antes
                </p>
                <p className="text-xs text-muted-foreground">
                  Sua vaga pode ser oferecida para outros jogadores se não confirmar
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceCard;